import React, { useEffect, useRef, useState } from 'react';
import { T, FONT_MONO } from '../styleEditorial';

/**
 * premiumKit.jsx — Composants animés / illustrations pour rendre finanalyse.fr
 * plus premium sans tomber dans le gadget SaaS.
 *
 * Exports :
 *   - useInView()              : hook intersection observer
 *   - <RevealOnScroll />       : fade-up cascade au scroll
 *   - <Counter />              : tick numérique animé (200+, 4,9/5, 48h)
 *   - <MagneticButton />       : CTA qui suit légèrement la souris
 *   - <ScrollProgressBar />    : fine ligne or en haut, progression de lecture
 *   - <Guilloche />            : motif SVG style gravure billet de banque
 *   - <DCFSchema />            : illustration de la méthode DCF qui se trace
 *   - <ValuationRangeChart />  : fourchette de valorisation pondérée
 */

// ════════════════════════════════════════════════════════
// HOOK : useInView — détecte quand un élément entre dans le viewport
// ════════════════════════════════════════════════════════
export function useInView({ threshold = 0.2, rootMargin = '0px', once = true } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    // Fallback : si IntersectionObserver indisponible, on affiche tout de suite
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    // Pré-rendu / bots (Puppeteer expose navigator.webdriver) et reduced-motion :
    // on n'attend PAS le scroll → tout le contenu est visible dans le HTML statique
    // (SEO) et pour les utilisateurs qui coupent les animations.
    if (
      (typeof navigator !== 'undefined' && navigator.webdriver) ||
      (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    ) {
      setInView(true);
      return;
    }
    const el = ref.current;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) obs.unobserve(el);
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, rootMargin, once]);

  return [ref, inView];
}

// ════════════════════════════════════════════════════════
// <RevealOnScroll /> — fade-up + translateY au scroll
// ════════════════════════════════════════════════════════
export function RevealOnScroll({
  children,
  delay = 0,
  duration = 700,
  translateY = 24,
  as: Tag = 'div',
  style = {},
  ...rest
}) {
  const [ref, inView] = useInView({ threshold: 0.15 });

  return (
    <Tag
      ref={ref}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : `translateY(${translateY}px)`,
        transition: `opacity ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// ════════════════════════════════════════════════════════
// <Counter /> — chiffres animés au visible (tick numérique)
//   Props :
//     from   = valeur de départ (default 0)
//     to     = valeur finale (number)
//     suffix = "+", "h", "%", "/5"…
//     prefix = ""
//     decimals = 0|1 (pour "4,9/5" → decimals: 1)
//     duration = 1600 (ms)
//     locale = 'fr-FR' (pour la virgule décimale)
//
//   SEO / pré-rendu (prerender.js) : le PREMIER rendu affiche la valeur
//   FINALE formatée (« 200+ », « 4,9/5 »…), jamais 0. Le HTML statique
//   capturé par Puppeteer — celui que voient Google et les visiteurs
//   avant hydratation — contient donc les vrais chiffres. L'animation
//   0 → N ne se joue que côté client, pour un vrai visiteur : jamais si
//   navigator.webdriver est vrai (Puppeteer/bots), jamais si
//   prefers-reduced-motion, et seulement à l'entrée dans le viewport.
// ════════════════════════════════════════════════════════
export function Counter({
  from = 0,
  to,
  suffix = '',
  prefix = '',
  decimals = 0,
  duration = 1600,
  locale = 'fr-FR',
  style = {},
  ...rest
}) {
  const [ref, inView] = useInView({ threshold: 0.4 });
  // État initial = valeur FINALE : pas de « 0+ » au premier paint ni dans
  // le snapshot Puppeteer, et pas de mismatch d'hydratation.
  const [value, setValue] = useState(typeof to === 'number' ? to : from);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!inView || startedRef.current) return;
    // Pré-rendu / bot : Puppeteer expose navigator.webdriver === true →
    // on n'anime JAMAIS, la valeur finale reste affichée.
    if (window.navigator.webdriver) return;
    // Accessibilité : prefers-reduced-motion → valeur finale fixe, sans anim.
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }
    startedRef.current = true;

    const startTime = performance.now();
    const range = to - from;
    let frame;

    function tick(now) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = from + range * eased;
      setValue(current);
      if (progress < 1) frame = requestAnimationFrame(tick);
      else setValue(to); // snap final
    }
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, from, to, duration]);

  const formatted = value.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  // UN SEUL nœud texte, et pas `{prefix}{formatted}{suffix}` (= 3 nœuds côté React).
  // Le pré-rendu Puppeteer sérialise le DOM via page.content() : la sérialisation HTML
  // fusionne les nœuds texte adjacents (« 48 » + « h » → « 48 h »). React, lui, en
  // attendait trois à l'hydratation → mismatch → React #418 sur CHAQUE compteur, et
  // bascule de la racine entière en rendu client (#423). Une seule chaîne = un seul
  // nœud des deux côtés.
  return (
    <span ref={ref} style={{ fontFamily: FONT_MONO, fontVariantNumeric: 'tabular-nums', ...style }} {...rest}>
      {`${prefix}${formatted}${suffix}`}
    </span>
  );
}

// ════════════════════════════════════════════════════════
// <MagneticButton /> — CTA qui attire la souris (10-12px max)
//   Wraps un <a> ou <button>. Désactivé sur touch.
// ════════════════════════════════════════════════════════
export function MagneticButton({
  children,
  as: Tag = 'a',
  strength = 0.25, // 0-1, plus c'est haut plus c'est tiré
  maxOffset = 12,
  style = {},
  ...rest
}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Désactive sur touch device
    if (window.matchMedia('(pointer: coarse)').matches) return;

    let frame;
    const handleMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) * strength;
      const dy = (e.clientY - cy) * strength;
      const clampedX = Math.max(-maxOffset, Math.min(maxOffset, dx));
      const clampedY = Math.max(-maxOffset, Math.min(maxOffset, dy));
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        el.style.transform = `translate(${clampedX}px, ${clampedY}px)`;
      });
    };

    const handleLeave = () => {
      cancelAnimationFrame(frame);
      el.style.transform = 'translate(0, 0)';
    };

    el.addEventListener('mousemove', handleMove);
    el.addEventListener('mouseleave', handleLeave);
    return () => {
      el.removeEventListener('mousemove', handleMove);
      el.removeEventListener('mouseleave', handleLeave);
      cancelAnimationFrame(frame);
    };
  }, [strength, maxOffset]);

  return (
    <Tag
      ref={ref}
      style={{
        display: 'inline-flex',
        transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        willChange: 'transform',
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// ════════════════════════════════════════════════════════
// <ScrollProgressBar /> — fine ligne or top, progression page
// ════════════════════════════════════════════════════════
export function ScrollProgressBar({ color = T.gold, height = 2 } = {}) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const update = () => {
      const scrollTop = window.scrollY || window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, pct)));
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        height: `${height}px`,
        background: 'transparent',
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      <div style={{
        height: '100%',
        width: `${progress}%`,
        background: color,
        transition: 'width 0.1s ease-out',
      }} />
    </div>
  );
}

// ════════════════════════════════════════════════════════
// <Guilloche /> — motif SVG style gravure billet de banque
//   Pattern de cercles concentriques fins, en arrière-plan
// ════════════════════════════════════════════════════════
export function Guilloche({
  size = 280,
  color = T.gold,
  opacity = 0.18,
  rings = 12,
  style = {},
}) {
  const center = size / 2;
  const maxRadius = size / 2 - 4;
  const ringsArr = Array.from({ length: rings }, (_, i) => {
    const r = (maxRadius / rings) * (i + 1);
    return r;
  });

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ pointerEvents: 'none', ...style }}
      aria-hidden="true"
    >
      <defs>
        {/* Gradient subtil pour les rings extérieurs (estompe vers les bords) */}
        <radialGradient id="guillocheFade" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={color} stopOpacity={opacity * 1.4} />
          <stop offset="70%" stopColor={color} stopOpacity={opacity} />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Rings concentriques */}
      {ringsArr.map((r, i) => (
        <circle
          key={i}
          cx={center} cy={center} r={r}
          fill="none"
          stroke="url(#guillocheFade)"
          strokeWidth={0.5}
        />
      ))}
      {/* Rosettes : 24 lignes radiales fines */}
      {Array.from({ length: 36 }).map((_, i) => {
        const angle = (i * Math.PI * 2) / 36;
        const x2 = center + Math.cos(angle) * maxRadius;
        const y2 = center + Math.sin(angle) * maxRadius;
        return (
          <line
            key={i}
            x1={center} y1={center}
            x2={x2} y2={y2}
            stroke={color}
            strokeWidth={0.3}
            opacity={opacity * 0.5}
          />
        );
      })}
      {/* Cercle central plein subtil */}
      <circle cx={center} cy={center} r={2} fill={color} opacity={opacity * 2} />
    </svg>
  );
}

// ════════════════════════════════════════════════════════
// <DCFSchema /> — Illustration de la méthode DCF
//   Flux d'années + valeur terminale → valorisation
//   Trait animé qui se dessine au scroll
// ════════════════════════════════════════════════════════
export function DCFSchema({ width = 380, height = 200, animated = true }) {
  const [ref, inView] = useInView({ threshold: 0.3 });
  const years = [
    { y: 'Y1', val: 120 },
    { y: 'Y2', val: 140 },
    { y: 'Y3', val: 165 },
    { y: 'Y4', val: 185 },
    { y: 'Y5', val: 200 },
    { y: 'VT', val: 280 }, // Valeur terminale
  ];
  const maxVal = Math.max(...years.map(y => y.val));
  const barW = 28;
  const gap = 24;
  const totalW = years.length * (barW + gap) - gap;
  const startX = (width - totalW) / 2;
  const baseY = height - 32;
  const maxBarH = height - 60;

  return (
    <svg ref={ref} width="100%" viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }} aria-label="Schéma méthode DCF">
      {/* Ligne de base */}
      <line x1={20} y1={baseY} x2={width - 20} y2={baseY} stroke={T.ink} strokeWidth={1} opacity={0.4} />

      {/* Barres animées */}
      {years.map((y, i) => {
        const h = (y.val / maxVal) * maxBarH;
        const x = startX + i * (barW + gap);
        const isVT = y.y === 'VT';
        return (
          <g key={i}>
            <rect
              x={x}
              y={baseY - h}
              width={barW}
              height={animated && inView ? h : (animated ? 0 : h)}
              fill={isVT ? T.gold : T.ink}
              opacity={isVT ? 1 : 0.92}
              style={{
                transition: `height 0.9s cubic-bezier(0.16, 1, 0.3, 1) ${i * 120}ms, y 0.9s cubic-bezier(0.16, 1, 0.3, 1) ${i * 120}ms`,
              }}
            />
            {/* Valeur en haut de la barre */}
            <text
              x={x + barW / 2}
              y={baseY - h - 8}
              fill={T.ink}
              fontFamily="'JetBrains Mono', monospace"
              fontSize="9"
              textAnchor="middle"
              opacity={animated && inView ? 1 : 0}
              style={{ transition: `opacity 0.5s ease ${i * 120 + 400}ms` }}
            >
              {y.val}
            </text>
            {/* Année en bas */}
            <text
              x={x + barW / 2}
              y={baseY + 16}
              fill={T.muted}
              fontFamily="'JetBrains Mono', monospace"
              fontSize="10"
              textAnchor="middle"
              fontWeight={isVT ? 600 : 400}
            >
              {y.y}
            </text>
          </g>
        );
      })}

      {/* Label méthode */}
      <text x={20} y={20} fill={T.gold} fontFamily="'Inter Tight', sans-serif" fontSize="9" fontWeight="600" letterSpacing="2px">DCF · FLUX ACTUALISÉS</text>

      {/* Flèche valeur finale */}
      <text x={width - 20} y={20} fill={T.ink} fontFamily="'JetBrains Mono', monospace" fontSize="11" fontWeight="500" textAnchor="end" opacity={animated && inView ? 1 : 0} style={{ transition: 'opacity 0.6s ease 1.2s' }}>
        → 495 K€
      </text>
    </svg>
  );
}

// ════════════════════════════════════════════════════════
// <ValuationRangeChart /> — fourchette pondérée 3 méthodes
// ════════════════════════════════════════════════════════
export function ValuationRangeChart({ width = 380, height = 164 }) {
  const [ref, inView] = useInView({ threshold: 0.3 });

  const methods = [
    { label: 'ANR',        low: 410, high: 450, weight: 15 },
    { label: 'Multiples',  low: 460, high: 530, weight: 60 },
    { label: 'DCF',        low: 510, high: 600, weight: 25 },
  ];
  const min = 400, max = 620;
  const range = max - min;
  const barH = 18;
  const gap = 14;
  const trackX = 90;
  const trackW = width - trackX - 30;

  return (
    <svg ref={ref} width="100%" viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }} aria-label="Fourchette de valorisation pondérée">
      {/* Label */}
      <text x={20} y={20} fill={T.gold} fontFamily="'Inter Tight', sans-serif" fontSize="9" fontWeight="600" letterSpacing="2px">FOURCHETTE PONDÉRÉE</text>

      {methods.map((m, i) => {
        const y = 40 + i * (barH + gap);
        const xLow = trackX + ((m.low - min) / range) * trackW;
        const xHigh = trackX + ((m.high - min) / range) * trackW;
        const w = xHigh - xLow;
        const delay = i * 150;

        return (
          <g key={i}>
            {/* Label méthode */}
            <text x={trackX - 8} y={y + barH / 2 + 4} fill={T.ink} fontFamily="'Inter Tight', sans-serif" fontSize="11" fontWeight="500" textAnchor="end">{m.label}</text>

            {/* Track background */}
            <rect x={trackX} y={y} width={trackW} height={barH} fill={T.ruleLight} opacity={0.5} />

            {/* Bar value */}
            <rect
              x={inView ? xLow : trackX}
              y={y}
              width={inView ? w : 0}
              height={barH}
              fill={T.ink}
              opacity={0.92}
              style={{ transition: `x 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, width 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms` }}
            />

            {/* Poids */}
            {/* Une seule chaîne : `{m.weight}%` produirait 2 nœuds texte côté React
                là où le HTML pré-rendu n'en sérialise qu'un → mismatch d'hydratation. */}
            <text x={width - 8} y={y + barH / 2 + 4} fill={T.gold} fontFamily="'JetBrains Mono', monospace" fontSize="11" fontWeight="500" textAnchor="end">
              {`${m.weight}%`}
            </text>
          </g>
        );
      })}

      {/* Valeur centrale en bas */}
      <text x={trackX + trackW / 2} y={height - 8} fill={T.ink} fontFamily="'JetBrains Mono', monospace" fontSize="14" fontWeight="500" textAnchor="middle" opacity={inView ? 1 : 0} style={{ transition: 'opacity 0.5s ease 0.8s' }}>
        Valeur centrale : 495 K€
      </text>
    </svg>
  );
}
